import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import config from "#config";
import { getUser } from "src/redux/selectors";
import { logAndSendError } from "src/utils/errors";

const ANALYTICS_HOST =
    "cz2pv2v5ha6xwlgp72o7uu7tde0rgalo.lambda-url.us-east-1.on.aws";

export function useIdentify() {
    const user = useSelector(getUser);
    const lastIdentity = useRef<string | null>(null);

    useEffect(() => {
        initSegmentAnalytics();
    }, []);

    useEffect(() => {
        if (!user?.uid) {
            return;
        }

        window.Intercom("boot", {
            app_id: "z0np13lu",
            email: user.email,
            user_id: user.uid,
            user_hash: user.intercomHash,
            name: user.name,
            "Restaurant Dashboard User": true,
            hide_default_launcher: true,
        });
        if (!user.intercomHash) console.warn("No intercom hash found for user");
    }, [user?.uid, user?.intercomHash]);

    useEffect(() => {
        if (!user?.uid) return;
        Sentry.configureScope((scope) => {
            scope.setUser({
                email: user.email,
                id: user.uid || "",
                uid: user.uid,
                name: user.name,
            });
        });

        try {
            const traits = {
                email: user.email,
                id: user._id,
                uid: user.uid,
                name: user.name,
            };
            const signature = JSON.stringify(traits);
            if (signature !== lastIdentity.current) {
                window.analytics?.identify(user.uid, traits);
                lastIdentity.current = signature;
            }
        } catch (e) {
            logAndSendError("Error identifying user with Segment" + e);
        }
    }, [user?.uid, user?._id, user?.email, user?.name, user?.intercomHash]);
}

// the majority of this method was copy and pasted from segment
// documentation; it was moved from its normal place in the public
// html because of the added flexibility of using a custom
// and independent development data pipeline
// https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/quickstart/#step-2-copy-the-segment-snippet
function initSegmentAnalytics() {
    try {
        const API_KEY = config.PRODUCTION_SEGMENT_API_KEY;

        const analytics: any = (window.analytics = window.analytics || []);
        if (!analytics.initialize)
            if (analytics.invoked)
                window.console &&
                    logAndSendError &&
                    logAndSendError("Segment snippet included twice.");
            else {
                analytics.invoked = !0;
                analytics.methods = [
                    "trackSubmit",
                    "trackClick",
                    "trackLink",
                    "trackForm",
                    "pageview",
                    "identify",
                    "reset",
                    "group",
                    "track",
                    "ready",
                    "alias",
                    "debug",
                    "page",
                    "once",
                    "off",
                    "on",
                ];
                analytics.factory = function (t: any) {
                    return function () {
                        const e = Array.prototype.slice.call(arguments);
                        e.unshift(t);
                        analytics.push(e);
                        return analytics;
                    };
                };
                for (let t = 0; t < analytics.methods.length; t++) {
                    const e = analytics.methods[t];
                    analytics[e] = analytics.factory(e);
                }
                analytics.load = function (t: any, e: any) {
                    const n = document.createElement("script");
                    n.type = "text/javascript";
                    n.async = !0;
                    n.src =
                        "https://" +
                        ANALYTICS_HOST +
                        "/analytics.js/v1/" +
                        t +
                        "/analytics.min.js";
                    const a = document.getElementsByTagName("script")[0];
                    a.parentNode?.insertBefore(n, a);
                    analytics._loadOptions = e;
                };
                analytics.SNIPPET_VERSION = "4.1.0";
                analytics.load(API_KEY, {
                    retryQueue: true,
                    integrations: {
                        "Segment.io": {
                            apiHost: ANALYTICS_HOST + "/v1",
                            protocol: "https",
                        },
                    },
                });
                analytics.page();
            }
    } catch (e) {
        logAndSendError("Error initializing Segment" + e);
    }
}
